import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { TRACKING_FUNCTION_CONNECT_SIP_ORDER, TrackingEntry } from './tracking-api.model';

/** How far back the SIP order link history reaches. */
export const SIP_ORDER_LINK_HISTORY_DAYS = 90;

/**
 * Formats a date as the offset-less local `yyyy-MM-ddTHH:mm:ss` the tracking API binds to
 * `LocalDateTime` (a trailing `Z` or offset is not accepted there).
 */
export function toTrackingDateTime(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    + `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

/**
 * Read client for the customersupport tracking API. AMIE only reads; the backends write.
 *
 * @ticket PS-6586
 */
@Injectable({ providedIn: 'root' })
export class TrackingApiService {
  private readonly http = inject(HttpClient);

  /**
   * Loads the `connect_sip_order` entries of the last {@link SIP_ORDER_LINK_HISTORY_DAYS} days.
   * `from` and `to` are sent on purpose: without them the API answers 204 even when rows exist
   * (verified on test 2026-10-14). `to` is one day ahead so a just-written entry is never cut off
   * by clock skew. A 204 (no body) yields `[]`.
   */
  getSipOrderLinkEntries(userAccountId: number, now: Date = new Date()): Observable<TrackingEntry[]> {
    const from = new Date(now);
    from.setDate(from.getDate() - SIP_ORDER_LINK_HISTORY_DAYS);
    const to = new Date(now);
    to.setDate(to.getDate() + 1);
    return this.http.get<TrackingEntry[] | null>(`${environment.customerSupportUrl}/tracking/${userAccountId}`, {
      params: {
        function: TRACKING_FUNCTION_CONNECT_SIP_ORDER,
        from: toTrackingDateTime(from),
        to: toTrackingDateTime(to),
      },
    }).pipe(map(entries => entries ?? []));
  }
}
